import {
  Business,
  MonetizationOn,
  SwapHoriz,
  Search,
  Create,
  Edit,
  AutoAwesome,
  ErrorOutline,
  HelpOutline,
} from '@mui/icons-material'

export enum ActivityType {
  ORG_CREATED = 'org_created',
  DISCOUNT_APPLIED = 'discount_applied',
  MEMBERSHIP_CHANGE = 'membership_change',
  SCAN_CREATED = 'scan_created',
  APPLICATION_CREATED = 'application_created',
  APPLICATION_UPDATED = 'application_updated',
  NEED_CREATED = 'need_created',
  NEED_UPDATED = 'need_updated',
  TIER_CHANGE = 'tier_change',
}

// Profile-update activity types the backend emits. They're intentionally NOT in
// ActivityType (so they get no filter buttons) — they're filed under the
// "Organizations" filter via ACTIVITY_CATEGORY_ALIASES below, but keep their own
// icon/colour and a readable card title (via formatActivityType).
export const ACTIVITY_ICONS = {
  [ActivityType.ORG_CREATED]: Business,
  [ActivityType.DISCOUNT_APPLIED]: MonetizationOn,
  [ActivityType.MEMBERSHIP_CHANGE]: SwapHoriz,
  [ActivityType.SCAN_CREATED]: Search,
  [ActivityType.APPLICATION_CREATED]: Create,
  [ActivityType.APPLICATION_UPDATED]: Edit,
  [ActivityType.NEED_CREATED]: Create,
  [ActivityType.NEED_UPDATED]: Edit,
  [ActivityType.TIER_CHANGE]: SwapHoriz,
  profile_updated: AutoAwesome,
  profile_update_failed: ErrorOutline,
  profile_needs_info: HelpOutline,
} as const

export const ACTIVITY_COLORS = {
  [ActivityType.ORG_CREATED]: '#4caf50',
  [ActivityType.DISCOUNT_APPLIED]: '#ff9800',
  [ActivityType.MEMBERSHIP_CHANGE]: '#2196f3',
  [ActivityType.SCAN_CREATED]: '#9c27b0',
  [ActivityType.APPLICATION_CREATED]: '#4caf50',
  [ActivityType.APPLICATION_UPDATED]: '#607d8b',
  [ActivityType.NEED_CREATED]: '#4caf50',
  [ActivityType.NEED_UPDATED]: '#607d8b',
  [ActivityType.TIER_CHANGE]: '#2196f3',
  profile_updated: '#1976d2',
  profile_update_failed: '#d32f2f',
  profile_needs_info: '#ed6c02',
} as const

// Activity types filed under an existing filter category rather than their own
// filter button. Profile events show under "Organizations".
export const ACTIVITY_CATEGORY_ALIASES: Record<string, ActivityType> = {
  profile_updated: ActivityType.ORG_CREATED,
  profile_update_failed: ActivityType.ORG_CREATED,
  profile_needs_info: ActivityType.ORG_CREATED,
}

// The filter category an activity belongs to (its alias, or itself).
export function activityFilterCategory(activityType: string): string {
  return ACTIVITY_CATEGORY_ALIASES[activityType] ?? activityType
}

export const ACTIVITY_LABELS = {
  [ActivityType.ORG_CREATED]: 'Organizations',
  [ActivityType.DISCOUNT_APPLIED]: 'Discounts',
  [ActivityType.MEMBERSHIP_CHANGE]: 'Status Changes',
  [ActivityType.SCAN_CREATED]: 'Scans',
  [ActivityType.APPLICATION_CREATED]: 'Applications',
  [ActivityType.APPLICATION_UPDATED]: 'Updates',
  [ActivityType.NEED_CREATED]: 'Needs Created',
  [ActivityType.NEED_UPDATED]: 'Needs Updated',
  [ActivityType.TIER_CHANGE]: 'Tier Changes',
} as const

export interface ActivityItem {
  activity_type: ActivityType | string // Allow string for backward compatibility
  activity_timestamp: string
  activity_details: string
  org_uuid?: string
  org_name?: string
  date?: string // Alias for activity_timestamp, used in some components
}
